import { z } from 'zod';

import { SentimentBandSchema } from './aspect-theme.js';
import {
  FeedbackFormFieldKindSchema,
  type FeedbackFormFieldKind,
} from './feedback-forms/field-schema.js';

// ---------------------------------------------------------------------------
// Playtests epic schemas.
//
// Mirrors public.playtests / playtest_events / playtest_questions /
// playtest_sessions / playtest_dm_dispatch as introduced in migration 0049.
//
// Playtest questions reuse the feedback-form field kind union and per-kind
// config shape so the bot's DM dispatch path renders both via one Discord
// modal builder. The only playtest-specific surface is the event row
// itself (name / headline / button label) plus the participation rule.
//
// Pairing primitives (player_tokens, pairing_codes) sit at the bottom of
// this file - they were defined in 0001 and the bot pairing flow (PR 2)
// is where the runtime shape gets exercised; we keep the row schemas here
// so every playtest-adjacent type lives in one module.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Question kind + config.
//
// Re-exported from the feedback-forms module so callers can reach the
// playtest question shape without juggling two imports. The bot uses the
// same render/validate path for both surfaces.
// ---------------------------------------------------------------------------

export const PlaytestQuestionKindSchema = FeedbackFormFieldKindSchema;
export type PlaytestQuestionKind = FeedbackFormFieldKind;

// Caps mirror the RPC validators in 0051. Keeping them as exported
// constants lets the portal pre-validate against the same limits.
export const PLAYTEST_QUESTION_PROMPT_MAX = 45;
export const PLAYTEST_QUESTION_HELP_TEXT_MAX = 100;
export const PLAYTEST_QUESTIONS_PER_EVENT_MAX = 5;
export const PLAYTEST_EVENTS_MAX = 50;
export const PLAYTEST_EVENT_NAME_MAX = 80;
export const PLAYTEST_EVENT_LABEL_MAX = 120;
export const PLAYTEST_EVENT_HEADLINE_MAX = 45;
export const PLAYTEST_EVENT_BUTTON_LABEL_MAX = 80;
export const PLAYTEST_NAME_MAX = 80;
export const DEFAULT_PLAYTEST_BUTTON_LABEL = 'Share feedback';

// ---------------------------------------------------------------------------
// PlaytestQuestion.
//
// Row-shape is the camelCase mapping of the playtest_questions table, with
// per-kind extras pulled out of the jsonb `config` column so the discriminated
// union is exhaustive at the call site (matches feedback_form_fields).
// ---------------------------------------------------------------------------

export const PlaytestQuestionSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  orderIndex: z.number().int().nonnegative(),
  kind: PlaytestQuestionKindSchema,
  prompt: z.string().min(1).max(PLAYTEST_QUESTION_PROMPT_MAX),
  helpText: z.string().nullable(),
  required: z.boolean(),
  // Per-kind shape lives here (mirrors FeedbackFormFieldConfigSchema). The
  // builder normalises this into typed slots; the bot reads the same blob.
  config: z.record(z.unknown()),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type PlaytestQuestion = z.infer<typeof PlaytestQuestionSchema>;

export const PlaytestQuestionRowSchema = z
  .object({
    id: z.string().uuid(),
    event_id: z.string().uuid(),
    order_index: z.number().int().nonnegative(),
    kind: PlaytestQuestionKindSchema,
    prompt: z.string(),
    help_text: z.string().nullable(),
    required: z.boolean(),
    config: z.record(z.unknown()).default({}),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .transform(
    (row): PlaytestQuestion => ({
      id: row.id,
      eventId: row.event_id,
      orderIndex: row.order_index,
      kind: row.kind,
      prompt: row.prompt,
      helpText: row.help_text,
      required: row.required,
      config: row.config,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }),
  );

// ---------------------------------------------------------------------------
// PlaytestEvent + events-with-questions composite.
//
// `headline` is what the tester sees in the DM bubble and as the title of
// the modal opened by the button. `buttonLabel` is the DM button copy
// (defaults to 'Share feedback'). `name` stays SDK-facing.
// ---------------------------------------------------------------------------

// Programming-identifier regex. JS / C# / C++ all share this lower-bound
// shape (^[A-Za-z_][A-Za-z0-9_]*$). The SDK snippets paste the name
// verbatim so pinning the shape here saves a runtime "won't compile"
// surprise later.
export const PLAYTEST_EVENT_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const PlaytestEventSchema = z.object({
  id: z.string().uuid(),
  playtestId: z.string().uuid(),
  orderIndex: z.number().int().nonnegative(),
  name: z.string().min(1).max(PLAYTEST_EVENT_NAME_MAX).regex(PLAYTEST_EVENT_NAME_PATTERN),
  label: z.string().min(1).max(PLAYTEST_EVENT_LABEL_MAX),
  description: z.string().nullable(),
  headline: z.string().max(PLAYTEST_EVENT_HEADLINE_MAX),
  buttonLabel: z.string().min(1).max(PLAYTEST_EVENT_BUTTON_LABEL_MAX),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type PlaytestEvent = z.infer<typeof PlaytestEventSchema>;

export const PlaytestEventRowSchema = z
  .object({
    id: z.string().uuid(),
    playtest_id: z.string().uuid(),
    order_index: z.number().int().nonnegative(),
    name: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    headline: z.string(),
    button_label: z.string(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .transform(
    (row): PlaytestEvent => ({
      id: row.id,
      playtestId: row.playtest_id,
      orderIndex: row.order_index,
      name: row.name,
      label: row.label,
      description: row.description,
      headline: row.headline,
      buttonLabel: row.button_label,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }),
  );

// "Tutorial Complete" -> "tutorialComplete". Strips characters outside
// [A-Za-z0-9], lower-cases the first surviving word, PascalCases the
// rest. If the result would start with a digit we prefix `_` so it
// still passes PLAYTEST_EVENT_NAME_PATTERN. An empty input returns ''.
export function derivePlaytestEventName(label: string): string {
  const parts = label.split(/[^A-Za-z0-9]+/).filter((part) => part.length > 0);
  if (parts.length === 0) return '';
  const [first, ...rest] = parts;
  const head = (first ?? '').toLowerCase();
  const tail = rest.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('');
  const combined = `${head}${tail}`;
  return /^[0-9]/.test(combined) ? `_${combined}` : combined;
}

export interface PlaytestEventWithQuestions extends PlaytestEvent {
  questions: PlaytestQuestion[];
}

// ---------------------------------------------------------------------------
// Playtest participation rule (tagged union).
// ---------------------------------------------------------------------------

export const PlaytestParticipationRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('open') }),
  z.object({
    kind: z.literal('channel_members'),
    channelIds: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    kind: z.literal('role_members'),
    roleIds: z.array(z.string().min(1)).min(1),
  }),
]);
export type PlaytestParticipationRule = z.infer<typeof PlaytestParticipationRuleSchema>;

export const DEFAULT_PLAYTEST_PARTICIPATION_RULE: PlaytestParticipationRule = {
  kind: 'open',
};

// ---------------------------------------------------------------------------
// Playtest.
// ---------------------------------------------------------------------------

export const PLAYTEST_STATUSES = ['draft', 'active', 'disabled'] as const;
export const PlaytestStatusSchema = z.enum(PLAYTEST_STATUSES);
export type PlaytestStatus = z.infer<typeof PlaytestStatusSchema>;

export const PlaytestSchema = z.object({
  id: z.string().uuid(),
  gameId: z.string().uuid(),
  studioId: z.string().uuid(),
  name: z.string().min(1).max(PLAYTEST_NAME_MAX),
  internalDescription: z.string().nullable(),
  status: PlaytestStatusSchema,
  participationRule: PlaytestParticipationRuleSchema,
  activatedAt: z.string().datetime({ offset: true }).nullable(),
  archivedAt: z.string().datetime({ offset: true }).nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Playtest = z.infer<typeof PlaytestSchema>;

export const PlaytestRowSchema = z
  .object({
    id: z.string().uuid(),
    game_id: z.string().uuid(),
    studio_id: z.string().uuid(),
    name: z.string(),
    internal_description: z.string().nullable(),
    status: PlaytestStatusSchema,
    participation_rule: z.unknown(),
    activated_at: z.string().datetime({ offset: true }).nullable(),
    archived_at: z.string().datetime({ offset: true }).nullable(),
    created_by_user_id: z.string().uuid().nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .transform((row): Playtest => {
    // Parse the jsonb participation rule defensively - a row written by an
    // older code path could carry an unexpected shape. Falls back to the
    // 'open' default so the surface remains usable rather than throwing.
    const parsedRule = PlaytestParticipationRuleSchema.safeParse(row.participation_rule);
    return {
      id: row.id,
      gameId: row.game_id,
      studioId: row.studio_id,
      name: row.name,
      internalDescription: row.internal_description,
      status: row.status,
      participationRule: parsedRule.success ? parsedRule.data : DEFAULT_PLAYTEST_PARTICIPATION_RULE,
      activatedAt: row.activated_at,
      archivedAt: row.archived_at,
      createdByUserId: row.created_by_user_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  });

// ---------------------------------------------------------------------------
// PlaytestRecruitmentConfig.
//
// Per-game recruitment-message config. Drives the Discord post the bot
// publishes on a cadence; players tap the button to receive a pairing
// code DM. One row per game.
// ---------------------------------------------------------------------------

export const PLAYTEST_RECRUITMENT_FREQUENCIES = ['never', 'daily', 'weekly', 'monthly'] as const;
export const PlaytestRecruitmentFrequencySchema = z.enum(PLAYTEST_RECRUITMENT_FREQUENCIES);
export type PlaytestRecruitmentFrequency = z.infer<typeof PlaytestRecruitmentFrequencySchema>;

export const PLAYTEST_RECRUITMENT_TITLE_MAX = 256;
export const PLAYTEST_RECRUITMENT_DESCRIPTION_MAX = 4096;
export const PLAYTEST_RECRUITMENT_BUTTON_LABEL_MAX = 80;
export const PLAYTEST_RECRUITMENT_INSTRUCTIONS_MAX = 4096;
export const PLAYTEST_RECRUITMENT_CHANNEL_MAX = 10;
export const DEFAULT_PLAYTEST_RECRUITMENT_BUTTON_LABEL = 'Register as tester';

export const PlaytestRecruitmentConfigSchema = z.object({
  id: z.string().uuid(),
  gameId: z.string().uuid(),
  studioId: z.string().uuid(),
  title: z.string().max(PLAYTEST_RECRUITMENT_TITLE_MAX),
  description: z.string().max(PLAYTEST_RECRUITMENT_DESCRIPTION_MAX),
  buttonLabel: z.string().min(1).max(PLAYTEST_RECRUITMENT_BUTTON_LABEL_MAX),
  repostFrequency: PlaytestRecruitmentFrequencySchema,
  channelIds: z.array(z.string().min(1)).max(PLAYTEST_RECRUITMENT_CHANNEL_MAX),
  pairingInstructions: z.string().max(PLAYTEST_RECRUITMENT_INSTRUCTIONS_MAX),
  enabled: z.boolean(),
  postedMessageIds: z.record(z.string()),
  lastPostedAt: z.string().datetime({ offset: true }).nullable(),
  lastRepostedAt: z.string().datetime({ offset: true }).nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type PlaytestRecruitmentConfig = z.infer<typeof PlaytestRecruitmentConfigSchema>;

export const PlaytestRecruitmentConfigRowSchema = z
  .object({
    id: z.string().uuid(),
    game_id: z.string().uuid(),
    studio_id: z.string().uuid(),
    title: z.string(),
    description: z.string(),
    button_label: z.string(),
    repost_frequency: PlaytestRecruitmentFrequencySchema,
    channel_ids: z.array(z.string()),
    pairing_instructions: z.string(),
    enabled: z.boolean(),
    posted_message_ids: z.unknown(),
    last_posted_at: z.string().datetime({ offset: true }).nullable(),
    last_reposted_at: z.string().datetime({ offset: true }).nullable(),
    created_by_user_id: z.string().uuid().nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .transform((row): PlaytestRecruitmentConfig => {
    // posted_message_ids is jsonb; coerce defensively so a missing /
    // malformed shape still parses (we don't read it in the portal,
    // only the bot does, but the row needs to round-trip cleanly).
    const posted =
      typeof row.posted_message_ids === 'object' &&
      row.posted_message_ids !== null &&
      !Array.isArray(row.posted_message_ids)
        ? (row.posted_message_ids as Record<string, unknown>)
        : {};
    const safePosted: Record<string, string> = {};
    for (const [key, value] of Object.entries(posted)) {
      if (typeof value === 'string') safePosted[key] = value;
    }
    return {
      id: row.id,
      gameId: row.game_id,
      studioId: row.studio_id,
      title: row.title,
      description: row.description,
      buttonLabel: row.button_label,
      repostFrequency: row.repost_frequency,
      channelIds: row.channel_ids,
      pairingInstructions: row.pairing_instructions,
      enabled: row.enabled,
      postedMessageIds: safePosted,
      lastPostedAt: row.last_posted_at,
      lastRepostedAt: row.last_reposted_at,
      createdByUserId: row.created_by_user_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  });

export interface PlaytestRecruitmentConfigInput {
  title: string;
  description: string;
  buttonLabel: string;
  repostFrequency: PlaytestRecruitmentFrequency;
  channelIds: string[];
  pairingInstructions: string;
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// PlaytestSession.
//
// Created by PR 3 on first matching event. Consumed by PR 5's session
// list + timeline.
// ---------------------------------------------------------------------------

export const PLAYTEST_SESSION_STATES = [
  'in_progress',
  'completed',
  'expired',
  'abandoned',
] as const;
export const PlaytestSessionStateSchema = z.enum(PLAYTEST_SESSION_STATES);
export type PlaytestSessionState = z.infer<typeof PlaytestSessionStateSchema>;

export const PlaytestSessionSchema = z.object({
  id: z.string().uuid(),
  playtestId: z.string().uuid(),
  studioId: z.string().uuid(),
  gameId: z.string().uuid(),
  playerTokenId: z.string().uuid().nullable(),
  discordUserId: z.string(),
  buildMarkerId: z.string().uuid().nullable(),
  state: PlaytestSessionStateSchema,
  startedAt: z.string().datetime({ offset: true }),
  lastActivityAt: z.string().datetime({ offset: true }),
  endedAt: z.string().datetime({ offset: true }).nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type PlaytestSession = z.infer<typeof PlaytestSessionSchema>;

export const PlaytestSessionRowSchema = z
  .object({
    id: z.string().uuid(),
    playtest_id: z.string().uuid(),
    studio_id: z.string().uuid(),
    game_id: z.string().uuid(),
    player_token_id: z.string().uuid().nullable(),
    discord_user_id: z.string(),
    build_marker_id: z.string().uuid().nullable(),
    state: PlaytestSessionStateSchema,
    started_at: z.string().datetime({ offset: true }),
    last_activity_at: z.string().datetime({ offset: true }),
    ended_at: z.string().datetime({ offset: true }).nullable(),
    metadata: z.record(z.unknown()).nullable(),
    created_at: z.string().datetime({ offset: true }),
  })
  .transform(
    (row): PlaytestSession => ({
      id: row.id,
      playtestId: row.playtest_id,
      studioId: row.studio_id,
      gameId: row.game_id,
      playerTokenId: row.player_token_id,
      discordUserId: row.discord_user_id,
      buildMarkerId: row.build_marker_id,
      state: row.state,
      startedAt: row.started_at,
      lastActivityAt: row.last_activity_at,
      endedAt: row.ended_at,
      metadata: row.metadata,
      createdAt: row.created_at,
    }),
  );

// ---------------------------------------------------------------------------
// PlaytestEventLog. Run-time audit row written by ingest_playtest_event
// (PR 3 / 0060). One row per accepted event ingestion; the session
// timeline (PR 5) merges these with dispatch rows and feedback_pool
// responses to render a chronological view.
// ---------------------------------------------------------------------------

export const PlaytestEventLogSchema = z.object({
  id: z.string().uuid(),
  playtestSessionId: z.string().uuid(),
  playtestId: z.string().uuid(),
  eventId: z.string().uuid(),
  eventName: z.string(),
  payload: z.record(z.unknown()),
  idempotencyKey: z.string().uuid(),
  playerTokenId: z.string().uuid(),
  occurredAt: z.string().datetime({ offset: true }),
  createdAt: z.string().datetime({ offset: true }),
});
export type PlaytestEventLog = z.infer<typeof PlaytestEventLogSchema>;

export const PlaytestEventLogRowSchema = z
  .object({
    id: z.string().uuid(),
    playtest_session_id: z.string().uuid(),
    playtest_id: z.string().uuid(),
    event_id: z.string().uuid(),
    event_name: z.string(),
    payload: z.record(z.unknown()),
    idempotency_key: z.string().uuid(),
    player_token_id: z.string().uuid(),
    occurred_at: z.string().datetime({ offset: true }),
    created_at: z.string().datetime({ offset: true }),
  })
  .transform(
    (row): PlaytestEventLog => ({
      id: row.id,
      playtestSessionId: row.playtest_session_id,
      playtestId: row.playtest_id,
      eventId: row.event_id,
      eventName: row.event_name,
      payload: row.payload,
      idempotencyKey: row.idempotency_key,
      playerTokenId: row.player_token_id,
      occurredAt: row.occurred_at,
      createdAt: row.created_at,
    }),
  );

// ---------------------------------------------------------------------------
// PlaytestDmDispatch. Per-event DM the bot delivers; tracks the modal
// lifecycle (pending → claimed → sent → responded | expired | failed).
// The PR 4 listener / handlers own writes; PR 5 reads to render dispatch
// cards in the session timeline.
// ---------------------------------------------------------------------------

export const PLAYTEST_DM_DISPATCH_STATES = [
  'pending',
  'claimed',
  'sent',
  'responded',
  'expired',
  'failed',
] as const;
export const PlaytestDmDispatchStateSchema = z.enum(PLAYTEST_DM_DISPATCH_STATES);
export type PlaytestDmDispatchState = z.infer<typeof PlaytestDmDispatchStateSchema>;

export const PlaytestDmDispatchSchema = z.object({
  id: z.string().uuid(),
  playtestSessionId: z.string().uuid(),
  questionId: z.string().uuid().nullable(),
  eventLogId: z.string().uuid().nullable(),
  discordUserId: z.string(),
  status: PlaytestDmDispatchStateSchema,
  ttlSeconds: z.number().int().nonnegative(),
  claimedAt: z.string().datetime({ offset: true }).nullable(),
  sentAt: z.string().datetime({ offset: true }).nullable(),
  respondedAt: z.string().datetime({ offset: true }).nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type PlaytestDmDispatch = z.infer<typeof PlaytestDmDispatchSchema>;

export const PlaytestDmDispatchRowSchema = z
  .object({
    id: z.string().uuid(),
    playtest_session_id: z.string().uuid(),
    question_id: z.string().uuid().nullable(),
    event_log_id: z.string().uuid().nullable(),
    discord_user_id: z.string(),
    status: PlaytestDmDispatchStateSchema,
    ttl_seconds: z.number().int().nonnegative(),
    claimed_at: z.string().datetime({ offset: true }).nullable(),
    sent_at: z.string().datetime({ offset: true }).nullable(),
    responded_at: z.string().datetime({ offset: true }).nullable(),
    last_error: z.string().nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .transform(
    (row): PlaytestDmDispatch => ({
      id: row.id,
      playtestSessionId: row.playtest_session_id,
      questionId: row.question_id,
      eventLogId: row.event_log_id,
      discordUserId: row.discord_user_id,
      status: row.status,
      ttlSeconds: row.ttl_seconds,
      claimedAt: row.claimed_at,
      sentAt: row.sent_at,
      respondedAt: row.responded_at,
      lastError: row.last_error,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }),
  );

// ---------------------------------------------------------------------------
// Pairing primitives.
// ---------------------------------------------------------------------------

export const PlayerTokenSchema = z.object({
  id: z.string().uuid(),
  gameId: z.string().uuid(),
  discordUserId: z.string(),
  displayName: z.string().nullable(),
  consentedToDmAt: z.string().datetime({ offset: true }).nullable(),
  pairedAt: z.string().datetime({ offset: true }),
  revokedAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type PlayerToken = z.infer<typeof PlayerTokenSchema>;

export const PairingCodeSchema = z.object({
  id: z.string().uuid(),
  gameId: z.string().uuid(),
  code: z.string().min(4),
  issuedToInstallId: z.string().nullable(),
  consumedByDiscordUserId: z.string().nullable(),
  consumedAt: z.string().datetime({ offset: true }).nullable(),
  expiresAt: z.string().datetime({ offset: true }),
  createdAt: z.string().datetime({ offset: true }),
});
export type PairingCode = z.infer<typeof PairingCodeSchema>;

// ---------------------------------------------------------------------------
// Builder-side helpers.
//
// Wire shape for replace_playtest_events / replace_event_questions. The
// per-kind config slots mirror FeedbackFormFieldsInput (one source of truth
// for the bot's modal builder).
// ---------------------------------------------------------------------------

export interface PlaytestEventInput {
  id?: string;
  // Portal label is required at the RPC; the SDK identifier (`name`) is
  // derived from it client-side.
  label: string;
  name: string;
  description?: string | null;
  headline?: string;
  buttonLabel?: string;
  questions?: PlaytestQuestionInput[];
}

export type PlaytestQuestionInput = {
  id?: string;
  kind: PlaytestQuestionKind;
  prompt: string;
  helpText?: string | null;
  required?: boolean;
  config?: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// PlaytestApiKey. One row per game; the HMAC secret game SDKs use to sign
// /pair and /events requests. The `key` column itself is service-role-only
// (column-level grant in migration 0060); members only ever see the
// metadata shape below. `rotate_playtest_api_key` returns the plaintext
// once, in the `PlaytestApiKeyRotateResult` shape.
// ---------------------------------------------------------------------------

export const PlaytestApiKeyRowSchema = z
  .object({
    id: z.string().uuid(),
    game_id: z.string().uuid(),
    studio_id: z.string().uuid(),
    key_prefix: z.string(),
    created_by_user_id: z.string().uuid().nullable(),
    created_at: z.string().datetime({ offset: true }),
    rotated_at: z.string().datetime({ offset: true }).nullable(),
  })
  .transform((row) => ({
    id: row.id,
    gameId: row.game_id,
    studioId: row.studio_id,
    keyPrefix: row.key_prefix,
    createdByUserId: row.created_by_user_id,
    createdAt: row.created_at,
    rotatedAt: row.rotated_at,
  }));
export type PlaytestApiKey = z.infer<typeof PlaytestApiKeyRowSchema>;

export const PlaytestApiKeyRotateResultSchema = z.object({
  key: z.string().min(1),
  key_prefix: z.string().min(1),
  rotated: z.boolean(),
});
export type PlaytestApiKeyRotateResult = z.infer<typeof PlaytestApiKeyRotateResultSchema>;

// --- Derived playtest sentiment (migration 0106) ---------------------------
//
// Read-side aggregates of the existing per-record sentiment enrichments on
// the session's answered-question feedback_pool rows. No new write path.

// get_playtest_session_sentiment(uuid) - one row. score/band null when no
// question row on the session has a current sentiment enrichment yet.
export const PlaytestSessionSentimentRowSchema = z.object({
  score: z.number().nullable(),
  band: SentimentBandSchema.nullable(),
});
export type PlaytestSessionSentiment = z.infer<typeof PlaytestSessionSentimentRowSchema>;

// get_playtest_sentiment_summary(uuid) - one row. Counts arrive as strings
// from PostgREST (bigint), so coerce.
export const PlaytestSentimentSummaryRowSchema = z
  .object({
    mean_score: z.number().nullable(),
    band: SentimentBandSchema.nullable(),
    session_count: z.coerce.number().int().nonnegative(),
    scored_count: z.coerce.number().int().nonnegative(),
  })
  .transform((row) => ({
    meanScore: row.mean_score,
    band: row.band,
    sessionCount: row.session_count,
    scoredCount: row.scored_count,
  }));
export type PlaytestSentimentSummary = z.infer<typeof PlaytestSentimentSummaryRowSchema>;

// --- Per-event flow aggregation (migration 0112) ---------------------------
//
// get_playtest_event_timeline(uuid) - one row per live event in a flow,
// ordered by order_index, powering the satisfaction timeline + summary cards.
// response_count arrives as a bigint string from PostgREST, so coerce.
// meanScore / band are null until the sentiment worker scores a submission,
// so they can lag responseCount; the portal renders a null band as
// "not scored yet".
export const PlaytestEventTimelineRowSchema = z
  .object({
    event_id: z.string().uuid(),
    label: z.string(),
    order_index: z.number().int(),
    response_count: z.coerce.number().int().nonnegative(),
    mean_score: z.number().nullable(),
    band: SentimentBandSchema.nullable(),
  })
  .transform((row) => ({
    eventId: row.event_id,
    label: row.label,
    orderIndex: row.order_index,
    responseCount: row.response_count,
    meanScore: row.mean_score,
    band: row.band,
  }));
export type PlaytestEventTimeline = z.infer<typeof PlaytestEventTimelineRowSchema>;

// get_playtest_event_field_summary(uuid) returns the SAME jsonb shape as
// get_form_field_summary, so the portal parses it with FormFieldSummaryRowSchema
// and renders it with the shared field-summary list. No separate schema here.
